import {
    colorForAgentId,
    configNameFromMetadata
} from '@/lib/deepIntelligenceAvatar';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import {
    ThreadHistory,
    useChatMessages,
    useChatSession
} from '@chainlit/react-client';

import {
    awaitingInputThreadIdsState,
    generatingThreadIdsState,
    unseenGeneratedThreadIdsState
} from '@/components/DMAnimations/threadGenerationState';
import { useThreadGenerationListener } from '@/components/DMAnimations/useThreadGenerationListener';
import { useTitleGeneratedListener } from '@/components/DMAnimations/useTitleGeneratedListener';
import { Checkbox } from '@/components/ui/checkbox';
import {
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar
} from '@/components/ui/sidebar';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@/components/ui/tooltip';

import { AnimatedThreadName } from '../DMAnimations/AnimatedThreadName';
import { Translator } from '../i18n';
import ThreadContextMenu from './ThreadContextMenu';
import ThreadOptions from './ThreadOptions';

interface FolderData {
    id: string;
    name: string;
    level: number;
}

interface TimeGroupedThreadListProps {
    threadHistory: ThreadHistory;
    sortedTimeGroupKeys: string[];
    folderedThreadIds: Set<string>;
    getTimeGroupLabel: (group: string) => React.ReactNode;
    onDragStart?: (e: React.DragEvent, threadId: string) => void;
    onDragEnd?: () => void;
    onDeleteThread?: (threadId: string) => void;
    onRenameThread?: (threadId: string, name: string) => void;
    onAddToProject?: (threadId: string, folderId: string) => void;
    onCreateNewProject?: (threadId?: string) => void;
    onRemoveFromProject?: (threadId: string) => void;
    onPinToOrg?: (threadId: string) => void;
    onShareThread?: (threadId: string) => void;
    folders?: FolderData[];
    // Touch event handlers for mobile
    onTouchStart?: (
        e: React.TouchEvent,
        threadId: string,
        type: 'thread'
    ) => void;
    onTouchMove?: (e: React.TouchEvent) => void;
    onTouchEnd?: (e: React.TouchEvent) => void;
    isMobile?: boolean;
    isDragging?: boolean;
    touchDraggedId?: string;
    // Multi-select mode props
    isMultiSelectMode?: boolean;
    selectedThreadIds?: Set<string>;
    onToggleSelection?: (threadId: string) => void;
    // Styling props
    showTooltips?: boolean;
    showThreadOptions?: boolean;
    itemClassName?: string;
    groupClassName?: string;
    // Dialog states
    threadIdToRename?: string;
}

export function TimeGroupedThreadList({
    threadHistory,
    sortedTimeGroupKeys,
    folderedThreadIds,
    getTimeGroupLabel,
    onDragStart,
    onDragEnd,
    onDeleteThread,
    onRenameThread,
    onAddToProject,
    onCreateNewProject,
    onRemoveFromProject,
    onPinToOrg,
    onShareThread,
    folders = [],
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    isMobile = false,
    isDragging = false,
    touchDraggedId,
    isMultiSelectMode = false,
    selectedThreadIds,
    onToggleSelection,
    showTooltips = true,
    showThreadOptions = true,
    itemClassName,
    groupClassName,
    threadIdToRename
}: TimeGroupedThreadListProps) {
    const { idToResume } = useChatSession();
    const { threadId: currentThreadId } = useChatMessages();
    const { pathname } = useLocation();
    const isOnThreadRoute = pathname.startsWith('/thread/');
    const { setOpenMobile, open, openMobile, isMobile: isMobileSidebar } =
        useSidebar();
    const isSidebarClosed = isMobileSidebar ? !openMobile : !open;

    // State for dropdown visibility
    const [openDropdownThreadId, setOpenDropdownThreadId] = useState<string>();
    const [contextMenuPosition, setContextMenuPosition] = useState<{
        x: number;
        y: number;
    } | null>(null);

    // Listen for title_generated events
    useTitleGeneratedListener();
    // Same-tab "still generating" spinner / "new response" dot on other
    // threads. Deliberately uses threadHistory.currentThreadId, not the
    // `currentThreadId` above (from useChatMessages) — that one only
    // updates once the server confirms resume_thread, which lags behind an
    // actual click. threadHistory.currentThreadId updates immediately on
    // route change (it's what drives isSelected/isResumed below), so it's
    // the only signal that reflects "which thread the user is looking at
    // right now" fast enough for the generating-vs-viewed check to matter.
    useThreadGenerationListener(threadHistory.currentThreadId);
    const generatingThreadIds = useRecoilValue(generatingThreadIdsState);
    const unseenGeneratedThreadIds = useRecoilValue(
        unseenGeneratedThreadIdsState
    );
    const awaitingInputThreadIds = useRecoilValue(awaitingInputThreadIdsState);

    const handleItemClick = useCallback(
        (e: React.MouseEvent, threadId: string) => {
            if (isMultiSelectMode && onToggleSelection) {
                e.preventDefault();
                onToggleSelection(threadId);
            }
        },
        [isMultiSelectMode, onToggleSelection]
    );

    const handleContextMenu = useCallback(
        (e: React.MouseEvent, threadId: string) => {
            e.preventDefault();
            setContextMenuPosition({ x: e.clientX, y: e.clientY });
            setOpenDropdownThreadId(threadId);
        },
        []
    );

    useEffect(() => {
        if (!isSidebarClosed) return;
        setOpenDropdownThreadId(undefined);
        setContextMenuPosition(null);
    }, [isSidebarClosed]);

    // Close context menu on outside click or escape
    useEffect(() => {
        const handleClickOutside = () => {
            if (openDropdownThreadId && contextMenuPosition) {
                setOpenDropdownThreadId(undefined);
                setContextMenuPosition(null);
            }
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (
                e.key === 'Escape' &&
                openDropdownThreadId &&
                contextMenuPosition
            ) {
                setOpenDropdownThreadId(undefined);
                setContextMenuPosition(null);
            }
        };

        if (openDropdownThreadId && contextMenuPosition) {
            document.addEventListener('click', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
            return () => {
                document.removeEventListener('click', handleClickOutside);
                document.removeEventListener('keydown', handleEscape);
            };
        }
    }, [openDropdownThreadId, contextMenuPosition]);

    // Check if there are any threads in folders
    const hasThreadsInFolders = folderedThreadIds.size > 0;

    // Check if all threads have been moved to projects
    const allThreadsInProjects = sortedTimeGroupKeys.every((group) => {
        const items = threadHistory.timeGroupedThreads![group];
        const availableItems = items.filter(
            (thread) => !folderedThreadIds.has(thread.id)
        );
        return availableItems.length === 0;
    });

    // If all threads are in projects AND there are actually threads in folders, show a helpful message
    if (allThreadsInProjects && hasThreadsInFolders) {
        return (
            <SidebarGroup className={cn('mb-6 p-0', groupClassName)}>
                <SidebarGroupContent>
                    <div className="px-3 py-6 text-center">
                        <div className="flex flex-col items-center gap-2">
                            <div className="font-medium">
                                All chats organized!
                            </div>
                            <div className="text-sm">
                                Your conversations have been moved to projects
                            </div>
                        </div>
                    </div>
                </SidebarGroupContent>
            </SidebarGroup>
        );
    }

    return (
        <>
            {sortedTimeGroupKeys.map((group) => {
                const items = threadHistory.timeGroupedThreads![group];
                const availableItems = items.filter(
                    (thread) => !folderedThreadIds.has(thread.id)
                );

                if (availableItems.length === 0) return null;

                return (
                    <SidebarGroup
                        key={group}
                        className={cn('mb-6 p-0', groupClassName)}
                    >
                        <SidebarGroupLabel
                            className={cn(
                                'px-3 font-normal! text-muted-foreground text-sm',
                                groupClassName
                            )}
                        >
                            {getTimeGroupLabel(group)}
                        </SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu className="gap-0">
                                {availableItems.map((thread) => {
                                    const isResumed =
                                        idToResume === thread.id &&
                                        !threadHistory.currentThreadId;
                                    const isSelected =
                                        isOnThreadRoute &&
                                        (isResumed ||
                                            threadHistory.currentThreadId ===
                                                thread.id);
                                    const isAwaitingInput =
                                        !isMultiSelectMode &&
                                        thread.id !==
                                            threadHistory.currentThreadId &&
                                        awaitingInputThreadIds.has(thread.id);
                                    const isGenerating =
                                        !isMultiSelectMode &&
                                        !isAwaitingInput &&
                                        thread.id !==
                                            threadHistory.currentThreadId &&
                                        generatingThreadIds.has(thread.id);
                                    const isUnread =
                                        !isMultiSelectMode &&
                                        thread.id !==
                                            threadHistory.currentThreadId &&
                                        unseenGeneratedThreadIds.has(thread.id);
                                    const isBeingTouchDragged =
                                        isMobile &&
                                        isDragging &&
                                        touchDraggedId === thread.id;

                                    const content = (
                                        <div
                                            className={cn(
                                                'flex gap-2 w-full',
                                                itemClassName,
                                                isMultiSelectMode
                                                    ? 'items-center pl-3'
                                                    : 'items-stretch'
                                            )}
                                        >
                                            {isMultiSelectMode &&
                                                selectedThreadIds &&
                                                onToggleSelection && (
                                                    <Checkbox
                                                        className="rounded-[5px] size-5"
                                                        iconClassName="size-3.5"
                                                        checked={selectedThreadIds.has(
                                                            thread.id
                                                        )}
                                                        onCheckedChange={() =>
                                                            onToggleSelection(
                                                                thread.id
                                                            )
                                                        }
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                    />
                                                )}

                                            <SidebarMenuButton
                                                isActive={
                                                    isSelected &&
                                                    !isMultiSelectMode
                                                }
                                                className={cn(
                                                    'group/thread relative items-stretch gap-2 h-9.5 pl-3 pr-1.25 rounded-lg w-full font-normal text-(length:--fs-title) [font-variation-settings:"wght"_400,"opsz"_14] data-[active=true]:[font-variation-settings:"wght"_500,"opsz"_14]',
                                                    ' transition-colors duration-200',
                                                    ' py-0',
                                                    'text-foreground',
                                                    openDropdownThreadId ===
                                                        thread.id &&
                                                        'bg-sidebar-accent text-foreground'
                                                )}
                                            >
                                                <div className="flex items-center gap-2 w-0 grow">
                                                    {!isMultiSelectMode && (
                                                    <span className="flex justify-center items-center size-3 shrink-0">
                                                        {isGenerating ? (
                                                            <span
                                                                key="generating"
                                                                className="flex animate-in duration-300 fade-in zoom-in-50"
                                                            >
                                                                <Loader2
                                                                    className="size-3 text-muted-foreground animate-spin"
                                                                    aria-label="Generating"
                                                                />
                                                            </span>
                                                        ) : (
                                                            <span
                                                                key="dot"
                                                                aria-label={
                                                                    isAwaitingInput
                                                                        ? 'Waiting for your answer'
                                                                        : isUnread
                                                                        ? 'New response ready'
                                                                        : undefined
                                                                }
                                                                className={cn(
                                                                    'rounded-full size-2 transition-colors animate-in duration-300 fade-in zoom-in-50',
                                                                    isAwaitingInput
                                                                        ? 'bg-orange-500'
                                                                        : isUnread
                                                                        ? 'bg-blue-500'
                                                                        : isSelected &&
                                                                          !isMultiSelectMode
                                                                        ? 'bg-foreground/60'
                                                                        : 'border border-muted-foreground/50'
                                                                )}
                                                            />
                                                        )}
                                                    </span>
                                                    )}
                                                    {(thread.metadata
                                                        ?.managed_agent_id ||
                                                        thread.metadata
                                                            ?.config_display_name) && (
                                                        <div
                                                            className="flex justify-center items-center rounded size-4 font-bold text-[9px] text-white shrink-0"
                                                            style={{
                                                                // Match the Feed's color key (config_id first,
                                                                // falling back to managed_agent_id) so a job's
                                                                // color stays consistent between the two pages —
                                                                // see StreamAnalysis.tsx's liveToDisplay.
                                                                backgroundColor:
                                                                    colorForAgentId(
                                                                        thread
                                                                            .metadata
                                                                            ?.config_id ??
                                                                            thread
                                                                                .metadata
                                                                                ?.managed_agent_id ??
                                                                            'deep-intelligence'
                                                                    )
                                                            }}
                                                            aria-label="Continued from a scheduled agent analysis"
                                                        >
                                                            {
                                                                configNameFromMetadata(
                                                                    thread.metadata
                                                                )[0]
                                                            }
                                                        </div>
                                                    )}
                                                    <div
                                                        key={thread.name}
                                                        className="truncate"
                                                    >
                                                        <AnimatedThreadName
                                                            threadId={thread.id}
                                                            name={thread.name}
                                                            fallback={
                                                                <Translator path="threadHistory.thread.untitled" />
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                                {/* <div
                          className={cn(
                            'top-0 right-0 bottom-0 absolute bg-linear-to-l from-[hsl(var(--sidebar-background))] to-transparent w-10'
                          )}
                        /> */}
                                                {showThreadOptions &&
                                                    onDeleteThread &&
                                                    onRenameThread &&
                                                    !isMultiSelectMode && (
                                                        <div
                                                            className={cn(
                                                                'flex items-stretch w-0 overflow-clip',
                                                                'group-hover/thread:flex',
                                                                'group-hover/thread:w-auto',

                                                                // Hide when context menu is open or rename dialog is open for this thread
                                                                isMobile
                                                                    ? 'w-auto'
                                                                    : (openDropdownThreadId ===
                                                                          thread.id &&
                                                                          contextMenuPosition) ||
                                                                      threadIdToRename ===
                                                                          thread.id
                                                                    ? 'pointer-events-none'
                                                                    : openDropdownThreadId ===
                                                                      thread.id
                                                                    ? 'w-auto'
                                                                    : ''
                                                            )}
                                                            onClick={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                        >
                                                            <ThreadOptions
                                                                onDelete={() =>
                                                                    onDeleteThread(
                                                                        thread.id
                                                                    )
                                                                }
                                                                onRename={() =>
                                                                    onRenameThread(
                                                                        thread.id,
                                                                        thread.name
                                                                    )
                                                                }
                                                                onShareThread={() =>
                                                                    onShareThread?.(
                                                                        thread.id
                                                                    )
                                                                }
                                                                onPinToOrg={
                                                                    onPinToOrg
                                                                        ? () =>
                                                                              onPinToOrg(
                                                                                  thread.id
                                                                              )
                                                                        : undefined
                                                                }
                                                                onAddToProject={
                                                                    onAddToProject
                                                                        ? (
                                                                              folderId: string
                                                                          ) =>
                                                                              onAddToProject(
                                                                                  thread.id,
                                                                                  folderId
                                                                              )
                                                                        : undefined
                                                                }
                                                                onCreateNewProject={
                                                                    onCreateNewProject
                                                                        ? () =>
                                                                              onCreateNewProject(
                                                                                  thread.id
                                                                              )
                                                                        : undefined
                                                                }
                                                                onPinToOrg={
                                                                    onPinToOrg
                                                                        ? () =>
                                                                              onPinToOrg(
                                                                                  thread.id
                                                                              )
                                                                        : undefined
                                                                }
                                                                folders={
                                                                    folders
                                                                }
                                                                isOpen={
                                                                    openDropdownThreadId ===
                                                                        thread.id &&
                                                                    !contextMenuPosition
                                                                }
                                                                onOpenChange={(
                                                                    open
                                                                ) => {
                                                                    if (
                                                                        !contextMenuPosition
                                                                    ) {
                                                                        setOpenDropdownThreadId(
                                                                            open
                                                                                ? thread.id
                                                                                : undefined
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                            </SidebarMenuButton>
                                        </div>
                                    );

                                    return (
                                        <SidebarMenuItem
                                            key={thread.id}
                                            id={`thread-${thread.id}`}
                                            draggable={
                                                !isMultiSelectMode &&
                                                !isMobile &&
                                                !!onDragStart
                                            }
                                            onDragStart={
                                                !isMultiSelectMode && !isMobile
                                                    ? (e) =>
                                                          onDragStart?.(
                                                              e,
                                                              thread.id
                                                          )
                                                    : undefined
                                            }
                                            onDragEnd={
                                                !isMultiSelectMode && !isMobile
                                                    ? onDragEnd
                                                    : undefined
                                            }
                                            className={cn(
                                                itemClassName,
                                                isBeingTouchDragged &&
                                                    isMobile &&
                                                    'opacity-30 scale-95 transition-all duration-150'
                                            )}
                                        >
                                            {isMultiSelectMode ? (
                                                <div
                                                    onClick={(e) =>
                                                        handleItemClick(
                                                            e,
                                                            thread.id
                                                        )
                                                    }
                                                    onContextMenu={(e) => {
                                                        if (isMobile) {
                                                            e.preventDefault();
                                                            return;
                                                        }
                                                        handleContextMenu(
                                                            e,
                                                            thread.id
                                                        );
                                                    }}
                                                    data-context-menu-trigger="true"
                                                    className="cursor-pointer"
                                                >
                                                    {content}
                                                </div>
                                            ) : showTooltips && false ? (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Link
                                                            to={
                                                                isResumed
                                                                    ? ''
                                                                    : `/thread/${thread.id}`
                                                            }
                                                            draggable={
                                                                !isMobile &&
                                                                !!onDragStart
                                                            }
                                                            onDragStart={
                                                                !isMobile
                                                                    ? (e) =>
                                                                          onDragStart?.(
                                                                              e,
                                                                              thread.id
                                                                          )
                                                                    : undefined
                                                            }
                                                            onDragEnd={
                                                                !isMobile
                                                                    ? onDragEnd
                                                                    : undefined
                                                            }
                                                            onTouchStart={
                                                                isMobile
                                                                    ? (e) =>
                                                                          onTouchStart?.(
                                                                              e,
                                                                              thread.id,
                                                                              'thread'
                                                                          )
                                                                    : undefined
                                                            }
                                                            onTouchMove={
                                                                isMobile
                                                                    ? onTouchMove
                                                                    : undefined
                                                            }
                                                            onTouchEnd={
                                                                isMobile
                                                                    ? onTouchEnd
                                                                    : undefined
                                                            }
                                                        >
                                                            {content}
                                                        </Link>
                                                    </TooltipTrigger>
                                                    <TooltipContent
                                                        side="right"
                                                        align="center"
                                                    >
                                                        <p>{thread.name}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            ) : (
                                                <Link
                                                    to={
                                                        isResumed
                                                            ? ''
                                                            : `/thread/${thread.id}`
                                                    }
                                                    onClick={
                                                        isMobile
                                                            ? () =>
                                                                  setOpenMobile(
                                                                      false
                                                                  )
                                                            : undefined
                                                    }
                                                    draggable={
                                                        !isMobile &&
                                                        !!onDragStart
                                                    }
                                                    onDragStart={
                                                        !isMobile
                                                            ? (e) =>
                                                                  onDragStart?.(
                                                                      e,
                                                                      thread.id
                                                                  )
                                                            : undefined
                                                    }
                                                    onDragEnd={
                                                        !isMobile
                                                            ? onDragEnd
                                                            : undefined
                                                    }
                                                    onTouchStart={
                                                        isMobile
                                                            ? (e) =>
                                                                  onTouchStart?.(
                                                                      e,
                                                                      thread.id,
                                                                      'thread'
                                                                  )
                                                            : undefined
                                                    }
                                                    onTouchMove={
                                                        isMobile
                                                            ? onTouchMove
                                                            : undefined
                                                    }
                                                    onTouchEnd={
                                                        isMobile
                                                            ? onTouchEnd
                                                            : undefined
                                                    }
                                                    onContextMenu={(e) => {
                                                        if (isMobile) {
                                                            e.preventDefault();
                                                            return;
                                                        }
                                                        handleContextMenu(
                                                            e,
                                                            thread.id
                                                        );
                                                    }}
                                                    data-context-menu-trigger="true"
                                                >
                                                    {content}
                                                </Link>
                                            )}
                                        </SidebarMenuItem>
                                    );
                                })}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                );
            })}

            {/* Context Menu Portal */}
            {openDropdownThreadId &&
                contextMenuPosition &&
                createPortal(
                    <div
                        className="z-50 fixed"
                        style={{
                            left: `${contextMenuPosition.x}px`,
                            top: `${contextMenuPosition.y}px`
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {(() => {
                            const thread = sortedTimeGroupKeys
                                .flatMap(
                                    (group) =>
                                        threadHistory.timeGroupedThreads![group]
                                )
                                .find((t) => t.id === openDropdownThreadId);

                            if (!thread) return null;

                            return (
                                <ThreadContextMenu
                                    onDelete={() => {
                                        onDeleteThread?.(thread.id);
                                        setContextMenuPosition(null);
                                        setOpenDropdownThreadId(undefined);
                                    }}
                                    onRename={() => {
                                        onRenameThread?.(
                                            thread.id,
                                            thread.name
                                        );
                                        setContextMenuPosition(null);
                                        setOpenDropdownThreadId(undefined);
                                    }}
                                    onShareThread={() => {
                                        onShareThread?.(thread.id);
                                        setContextMenuPosition(null);
                                        setOpenDropdownThreadId(undefined);
                                    }}
                                    onPinToOrg={
                                        onPinToOrg
                                            ? () => {
                                                  onPinToOrg(thread.id);
                                                  setContextMenuPosition(null);
                                                  setOpenDropdownThreadId(
                                                      undefined
                                                  );
                                              }
                                            : undefined
                                    }
                                    onAddToProject={
                                        onAddToProject
                                            ? (folderId: string) => {
                                                  onAddToProject(
                                                      thread.id,
                                                      folderId
                                                  );
                                                  setContextMenuPosition(null);
                                                  setOpenDropdownThreadId(
                                                      undefined
                                                  );
                                              }
                                            : undefined
                                    }
                                    onCreateNewProject={
                                        onCreateNewProject
                                            ? () => {
                                                  onCreateNewProject(thread.id);
                                                  setContextMenuPosition(null);
                                                  setOpenDropdownThreadId(
                                                      undefined
                                                  );
                                              }
                                            : undefined
                                    }
                                    onPinToOrg={
                                        onPinToOrg
                                            ? () => {
                                                  onPinToOrg(thread.id);
                                                  setContextMenuPosition(null);
                                                  setOpenDropdownThreadId(
                                                      undefined
                                                  );
                                              }
                                            : undefined
                                    }
                                    folders={folders}
                                    isInProject={!!thread.folderId}
                                    onRemoveFromProject={
                                        onRemoveFromProject
                                            ? () => {
                                                  onRemoveFromProject(
                                                      thread.id
                                                  );
                                                  setContextMenuPosition(null);
                                                  setOpenDropdownThreadId(
                                                      undefined
                                                  );
                                              }
                                            : undefined
                                    }
                                />
                            );
                        })()}
                    </div>,
                    document.body
                )}
        </>
    );
}
